import {
  isNotEmpty,
  isEmpty,
  type CardSlug,
  type CardThemeTokens,
  type CompanyCardSections,
  type PublicCompany,
  type Review,
} from '@terros/common'
import { usePublicCard } from '../usePublicCard'
import { type CompanyCardFormValues } from '../components/CompanyCardFields'

export type PreviewLayout = 'mobile' | 'desktop'

export const PREVIEW_LAYOUTS: PreviewLayout[] = ['mobile', 'desktop']

const MOBILE_DESIGN_WIDTH = 390
const DESKTOP_DESIGN_WIDTH = 1024

export type PreviewSocialLink = {
  key: string
  url: string
}

export type PreviewGalleryImage = {
  key: string
  url: string | undefined
}

export type CardPreviewContent = {
  companyName: string
  repAvatarUrl: string
  hasGoogleBusinessProfile: boolean
  rating: number | undefined
  reviewCount: number | undefined
  review: Review | undefined
  showTrust: boolean
  showCta: boolean
  showReviews: boolean
  showSocials: boolean
  socials: PreviewSocialLink[]
  gallery: PreviewGalleryImage[]
}

export type CardPreviewProps = {
  content: CardPreviewContent
  tokens: CardThemeTokens
}

export type PreviewReviewInfo = {
  rating: number | undefined
  reviewCount: number | undefined
  review: Review | undefined
}

type CardPreviewContentInput = {
  companyName: string
  repAvatarUrl: string
  card: CompanyCardFormValues
  gallerySlug: CardSlug | undefined
}

const ALL_SECTIONS: CompanyCardSections = { trust: true, gallery: true, reviews: true, cta: true, socials: true }

export function isPreviewLayout(value: string): value is PreviewLayout {
  return PREVIEW_LAYOUTS.some((layout) => layout === value)
}

export function previewDesignWidth(layout: PreviewLayout): number {
  if (layout === 'desktop') return DESKTOP_DESIGN_WIDTH
  return MOBILE_DESIGN_WIDTH
}

export function toBannerGradient(tokens: CardThemeTokens): string {
  return `linear-gradient(180deg, ${tokens.banner}, ${tokens.bannerEnd})`
}

export function toPreviewReviewInfo(
  placeId: string | undefined,
  company: Pick<PublicCompany, 'placeId' | 'rating' | 'ratingCount' | 'reviews'> | undefined
): PreviewReviewInfo {
  if (isEmpty(placeId)) return { rating: undefined, reviewCount: undefined, review: undefined }
  if (placeId !== company?.placeId) return { rating: undefined, reviewCount: undefined, review: undefined }
  return { rating: company.rating, reviewCount: company.ratingCount, review: company.reviews?.[0] }
}

export function useCardPreviewContent({
  companyName,
  repAvatarUrl,
  card,
  gallerySlug,
}: CardPreviewContentInput): CardPreviewContent {
  const sections = card.sections ?? ALL_SECTIONS
  const attachmentIds = (card.attachmentIds ?? []).slice(0, card.maxImagesPerCard)
  const publicCard = usePublicCard(gallerySlug)
  const galleryUrls = new Map(
    publicCard?.gallery
      .filter(({ attachmentId }) => attachmentIds.includes(attachmentId))
      .map(({ attachmentId, url }) => [attachmentId, url]) ?? []
  )
  const hasGoogleBusinessProfile = isNotEmpty(card.placeId)
  const reviewInfo = toPreviewReviewInfo(card.placeId, publicCard?.company)
  const socials = (card.links ?? []).flatMap((link) =>
    isNotEmpty(link.url) ? [{ key: `${link.label ?? ''}-${link.url}`, url: link.url }] : []
  )
  const gallery = sections.gallery
    ? attachmentIds.map((attachmentId) => ({ key: attachmentId, url: galleryUrls.get(attachmentId) }))
    : []
  return {
    companyName,
    repAvatarUrl,
    hasGoogleBusinessProfile,
    ...reviewInfo,
    showTrust: sections.trust,
    showCta: sections.cta && hasGoogleBusinessProfile,
    showReviews: sections.reviews,
    showSocials: sections.socials,
    socials,
    gallery,
  }
}
